import { useEffect, useRef, useState } from 'react';
import { newId, type ActionDto, type EquipmentFileDto, type ProcedureItem } from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { bytes, Icon, Sheet } from '../ui';
import { AuthImg, AuthVideo } from './AuthMedia';
import { PdfViewer } from './PdfViewer';

export const hasContent = (i: Pick<ProcedureItem, 'details' | 'fileIds'>) => !!i.details?.trim() || (i.fileIds?.length ?? 0) > 0;

/**
 * Пункты списка — отдельными полупрозрачными кнопками. Есть уточнение или файлы — справа значок,
 * нажатие открывает «как это сделать». Администратору нажатие открывает пункт всегда (чтобы дополнить).
 */
export function StepList({ items, onOpen, manage }: { items: ProcedureItem[]; onOpen: (i: ProcedureItem, n: number) => void; manage: boolean }) {
  return (
    <ol className="proc-steps">
      {items.map((i, n) => {
        const rich = hasContent(i);
        const open = rich || manage;
        return (
          <li key={i.id}>
            <button type="button" className={`step${rich ? ' rich' : ''}`} disabled={!open} onClick={() => onOpen(i, n)}>
              <span className="step-num">{n + 1}</span>
              <span className="grow">{i.text}</span>
              {i.actionId && manage && (
                <span className="step-more" aria-label="Типовое действие из библиотеки">
                  <Icon name="copy" size={14} />
                </span>
              )}
              {rich && (
                <span className="step-more" aria-label="Есть уточнение">
                  {(i.fileIds?.length ?? 0) > 0 ? <Icon name="photo" size={16} /> : <Icon name="info" size={16} />}
                  <Icon name="right" size={16} />
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Уточнение и файлы пункта: текст, фото, видео, PDF — всё внутри приложения. */
export function StepContent({ equipmentId, details, fileIds, files }: { equipmentId: string; details?: string | null; fileIds?: string[]; files: EquipmentFileDto[] }) {
  const [doc, setDoc] = useState<EquipmentFileDto | null>(null);
  const list = (fileIds ?? []).map((id) => files.find((f) => f.id === id)).filter((f): f is EquipmentFileDto => !!f);
  return (
    <>
      {details?.trim() && <div className="card flat text">{details}</div>}
      {list.filter((f) => f.kind === 'photo').map((f) => (
        <AuthImg key={f.id} path={f.url} alt={f.title} style={{ width: '100%', borderRadius: 12 }} />
      ))}
      {list.filter((f) => f.kind === 'video').map((f) => (
        <figure key={f.id} className="media-card">
          <AuthVideo path={f.url} size={f.size} />
        </figure>
      ))}
      {list.filter((f) => f.kind === 'doc').map((f) => (
        <button key={f.id} type="button" className="item" onClick={() => setDoc(f)}>
          <span className="mark">
            <Icon name="doc" />
          </span>
          <span className="grow stack" style={{ gap: 2 }}>
            <span className="title ellipsis">{f.title}</span>
            <span className="sub">PDF · {bytes(f.size)}</span>
          </span>
        </button>
      ))}
      {doc && <PdfViewer equipmentId={equipmentId} fileId={doc.id} title={doc.title} onClose={() => setDoc(null)} />}
    </>
  );
}

/**
 * Пункт: текст, уточнение «как сделать», фото/видео/PDF. Файлы загружаются сразу (помечены «к пункту»
 * и в общей галерее не видны); пункт сохраняется вместе со списком — onDone отдаёт изменённый пункт.
 */
export function StepEditor({
  equipmentId,
  item,
  files,
  onClose,
  onDone,
  onFile,
  library = false,
}: {
  /** Экран библиотеки: без кнопок «в библиотеку» / «отвязать». */
  library?: boolean;
  /** null — файлы общие (номенклатура, библиотека). */
  equipmentId: string | null;
  item: ProcedureItem;
  files: EquipmentFileDto[];
  onClose: () => void;
  onDone: (i: ProcedureItem) => void | Promise<void>;
  /** Новый загруженный файл — чтобы вызывающий знал его название и тип. */
  onFile: (f: EquipmentFileDto) => void;
}) {
  const { fail } = useApp();
  const [text, setText] = useState(item.text);
  const [details, setDetails] = useState(item.details ?? '');
  const [fileIds, setFileIds] = useState<string[]>(item.fileIds ?? []);
  const [upload, setUpload] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  async function pick(f: File | undefined) {
    if (input.current) input.current.value = '';
    if (!f) return;
    setUpload(0);
    try {
      const title = f.name.replace(/\.[^.]+$/, '').slice(0, 160) || 'Файл';
      // Типовое действие — файлы общие: оно может быть подключено к разным машинам и номенклатурам.
      const saved = equipmentId && !item.actionId ? await api.uploadEquipmentFile(equipmentId, f, title, setUpload, false, true) : await api.uploadSharedFile(f, title, setUpload);
      onFile(saved);
      setFileIds((ids) => [...ids, saved.id]);
      platform.haptic('success');
    } catch (e) {
      fail(e);
    } finally {
      setUpload(null);
    }
  }

  const [uses, setUses] = useState<number | null>(null);
  useEffect(() => {
    if (item.actionId) api.actions().then((l) => setUses(l.find((a) => a.id === item.actionId)?.uses ?? null)).catch(() => {});
  }, [item.actionId]);

  async function done(mode: 'save' | 'toLibrary' | 'unlink' = 'save') {
    if (!text.trim()) return fail(new Error('Текст пункта не может быть пустым'));
    setBusy(true);
    try {
      const content = { text: text.trim(), details: details.trim() || null, fileIds };
      if (mode === 'toLibrary') {
        const a = await api.createAction(content);
        return await onDone({ ...item, ...content, actionId: a.id });
      }
      if (mode === 'unlink') return await onDone({ ...item, ...content, actionId: null });
      // Пункт со ссылкой на библиотеку: правим само типовое действие — изменится везде.
      if (item.actionId) await api.updateAction(item.actionId, content);
      await onDone({ ...item, ...content });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const attached = fileIds.map((id) => files.find((f) => f.id === id)).filter((f): f is EquipmentFileDto => !!f);
  return (
    <Sheet title={item.actionId ? 'Типовое действие' : 'Пункт'} onClose={onClose}>
      {item.actionId && !library && (
        <div className="card flat small">
          Это действие из библиотеки{uses !== null ? `, подключено в ${uses} ${uses === 1 ? 'месте' : 'местах'}` : ''}. Изменения применятся везде. Чтобы поменять только здесь — «Отвязать и изменить только здесь».
        </div>
      )}
      <div className="field">
        <label htmlFor="st-text">Что сделать</label>
        <textarea id="st-text" className="input" rows={2} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="st-det">Как это сделать (откроется по нажатию на пункт)</label>
        <textarea
          id="st-det"
          className="input"
          rows={4}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Например: манометр справа от пульта, норма 6 бар. Ниже 5,5 — сообщить мастеру."
        />
      </div>
      <div className="field">
        <span className="label">Фото, видео, PDF к пункту</span>
        {attached.length > 0 && (
          <div className="list">
            {attached.map((f) => (
              <div key={f.id} className="item">
                <span className="mark">
                  <Icon name={f.kind === 'photo' ? 'photo' : f.kind === 'video' ? 'play' : 'doc'} />
                </span>
                <span className="grow stack" style={{ gap: 2 }}>
                  <span className="title ellipsis">{f.title}</span>
                  <span className="sub">{bytes(f.size)}</span>
                </span>
                <button type="button" className="icon-btn" aria-label={`Убрать «${f.title}»`} onClick={() => setFileIds((ids) => ids.filter((x) => x !== f.id))}>
                  <Icon name="trash" size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,application/pdf"
          hidden
          onChange={(e) => void pick(e.target.files?.[0])}
        />
        {upload !== null ? (
          <div className="progress" role="status" aria-label="Загрузка">
            <i style={{ width: `${Math.round(upload * 100)}%` }} />
          </div>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => input.current?.click()}>
            <Icon name="upload" size={16} />
            Добавить файл
          </button>
        )}
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy || upload !== null} onClick={() => void done()}>
        Готово
      </button>
      {library ? null : item.actionId ? (
        <button type="button" className="btn btn-ghost btn-block" disabled={busy || upload !== null} onClick={() => void done('unlink')}>
          Отвязать и изменить только здесь
        </button>
      ) : (
        <button type="button" className="btn btn-ghost btn-block" disabled={busy || upload !== null} onClick={() => void done('toLibrary')}>
          <Icon name="copy" size={16} />
          Сохранить в библиотеку типовых действий
        </button>
      )}
    </Sheet>
  );
}

/** Выбрать типовое действие из библиотеки — пункт подключается ссылкой (или копией). */
export function ActionPicker({ onPick, onClose }: { onPick: (i: ProcedureItem) => void; onClose: () => void }) {
  const { fail } = useApp();
  const [list, setList] = useState<ActionDto[] | null>(null);
  const [q, setQ] = useState('');
  const [copy, setCopy] = useState(false);
  useEffect(() => {
    api.actions().then(setList).catch(fail);
  }, [fail]);
  const shown = (list ?? []).filter((a) => !q.trim() || a.text.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Sheet title="Из библиотеки" onClose={onClose}>
      <input className="input" type="search" placeholder="Поиск действия" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск действия" />
      <label className="check">
        <input type="checkbox" checked={copy} onChange={(e) => setCopy(e.target.checked)} />
        <span>
          Вставить копией
          <span className="hint" style={{ display: 'block' }}>
            {copy ? 'Пункт можно править отдельно, библиотека его не изменит.' : 'Ссылкой: правка действия в библиотеке изменит его и здесь.'}
          </span>
        </span>
      </label>
      {!list && <div className="skeleton" />}
      {list && list.length === 0 && <p className="small muted">Библиотека пуста. Сохраните пункт в библиотеку из его настроек или заведите действия в «Ещё → Типовые действия».</p>}
      <div className="list">
        {shown.map((a) => (
          <button
            key={a.id}
            type="button"
            className="item"
            onClick={() => onPick({ id: newId('i'), text: a.text, details: a.details, fileIds: a.fileIds, actionId: copy ? null : a.id })}
          >
            <span className="grow stack" style={{ gap: 2 }}>
              <span className="title">{a.text}</span>
              <span className="sub ellipsis">
                {a.details ? a.details : 'без уточнения'}
                {a.fileIds.length ? ` · файлов: ${a.fileIds.length}` : ''} · подключено: {a.uses}
              </span>
            </span>
            <Icon name="plus" size={18} />
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/**
 * Редактор пунктов: текст, порядок (↑↓), «как сделать» и файлы (ⓘ), удаление,
 * добавление своего пункта или типового действия из библиотеки.
 */
export function ItemsEditor({
  label,
  items,
  onChange,
  equipmentId,
  files,
  onFile,
}: {
  label: string;
  items: ProcedureItem[];
  onChange: (items: ProcedureItem[]) => void;
  equipmentId: string | null;
  files: EquipmentFileDto[];
  onFile: (f: EquipmentFileDto) => void;
}) {
  const [edit, setEdit] = useState<ProcedureItem | null>(null);
  const [pick, setPick] = useState(false);
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    onChange(next);
  };
  return (
    <>
      {items.map((it, idx) => (
        <div key={it.id} className="item-row">
          <span className="num">{idx + 1}.</span>
          {it.actionId ? (
            <button type="button" className="input linked" onClick={() => setEdit(it)} aria-label={`${label}, пункт ${idx + 1} (типовое действие)`}>
              <Icon name="copy" size={14} /> {it.text}
            </button>
          ) : (
            <textarea
              className="input"
              rows={2}
              aria-label={`${label}, пункт ${idx + 1}`}
              value={it.text}
              onChange={(e) => onChange(items.map((x) => (x.id === it.id ? { ...x, text: e.target.value } : x)))}
            />
          )}
          <span className="tools">
            <button type="button" className="icon-btn" aria-label="Выше" disabled={idx === 0} onClick={() => move(idx, -1)}>
              <Icon name="up" size={16} />
            </button>
            <button type="button" className="icon-btn" aria-label="Ниже" disabled={idx === items.length - 1} onClick={() => move(idx, 1)}>
              <Icon name="down" size={16} />
            </button>
            <button type="button" className={`icon-btn${hasContent(it) ? ' on' : ''}`} aria-label={`Как сделать: пункт ${idx + 1}`} onClick={() => setEdit(it)}>
              <Icon name="info" size={16} />
            </button>
            <button type="button" className="icon-btn" aria-label={`Удалить пункт ${idx + 1}`} onClick={() => onChange(items.filter((x) => x.id !== it.id))}>
              <Icon name="trash" size={14} />
            </button>
          </span>
        </div>
      ))}
      <div className="grid-half">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...items, { id: newId('i'), text: '' }])}>
          <Icon name="plus" size={16} />
          Пункт в «{label}»
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPick(true)}>
          <Icon name="copy" size={16} />
          Из библиотеки
        </button>
      </div>
      {edit && (
        <StepEditor
          equipmentId={equipmentId}
          item={edit}
          files={files}
          onFile={onFile}
          onClose={() => setEdit(null)}
          onDone={(item) => {
            onChange(items.map((x) => (x.id === item.id ? item : x)));
            setEdit(null);
          }}
        />
      )}
      {pick && (
        <ActionPicker
          onClose={() => setPick(false)}
          onPick={(i) => {
            onChange([...items.filter((x) => x.text.trim() || x.actionId), i]);
            setPick(false);
          }}
        />
      )}
    </>
  );
}
